import {
  Directive,
  ElementRef,
  HostListener,
  Renderer2,
  AfterViewInit,
  Input,
  Host,
  OnDestroy,
  Output,
  Injectable,
  signal,
  computed,
  effect,
  EventEmitter,
} from '@angular/core';
import { Subscription } from 'rxjs';

// Service to manage dropdown states using signals
@Injectable({ providedIn: 'root' })
export class LearmoDropdownService {
  private openDropdowns = signal<Set<string>>(new Set());

  isDropdownOpen(dropdownId: string): boolean {
    return this.openDropdowns().has(dropdownId);
  }

  openDropdownState(dropdownId: string): void {
    this.openDropdowns.update((set) => new Set([...set, dropdownId]));
  }

  closeDropdownState(dropdownId: string): void {
    this.openDropdowns.update((set) => {
      const newSet = new Set(set);
      newSet.delete(dropdownId);
      return newSet;
    });
  }

  toggleDropdownState(dropdownId: string, forceState?: boolean): void {
    const isOpen = this.isDropdownOpen(dropdownId);
    const newState = forceState !== undefined ? forceState : !isOpen;
    if (newState) {
      this.openDropdownState(dropdownId);
    } else {
      this.closeDropdownState(dropdownId);
    }
  }

  closeAllDropdowns(except: string[] = []): void {
    this.openDropdowns().forEach((value: string) => {
      if (except.includes(value)) {
        return;
      }
      this.closeDropdownState(value);
      // Your logic here
    });
    // this.openDropdowns.update((set) => {
    //   if (except.length > 0) {
    //     return new Set(except.filter((id) => set.has(id)));
    //   } else {
    //     return new Set();
    //   }
    // });
  }
}

import { createPopper, Instance, Placement } from '@popperjs/core';

// Directive for the dropdown container
@Directive({
  selector: '[learmoDropdown]',
  exportAs: 'learmoDropdown',
})
export class LearmoDropdownDirective implements AfterViewInit, OnDestroy {
  @Input() autoClose: boolean = true;
  @Input() placement: Placement = 'bottom-start';
  @Input() container: 'body' | null = 'body';
  @Output() openChange = new EventEmitter<boolean>();

  private dropdownMenu: HTMLElement | null = null;
  private dropdownButton: HTMLElement | null = null;
  private dropdownId: string =
    String.fromCharCode(65 + Math.floor(Math.random() * 26)) + Date.now();
  private subscriptions: Subscription[] = [];
  public isOpen = signal<boolean>(false);
  private popperInstance: Instance | null = null;
  private resizeObserver: ResizeObserver | null = null;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private dropdownService: LearmoDropdownService,
  ) {
    // React to changes in the isOpen signal
    effect(() => {
      const isOpen = this.dropdownService.isDropdownOpen(this.dropdownId);
      if (isOpen) {
        if (this.dropdownMenu) {
          this.renderer.addClass(this.dropdownMenu, 'show');
          this.createPopperInstance();
        }
      } else {
        if (this.dropdownMenu) {
          this.renderer.removeClass(this.dropdownMenu, 'show');
          this.destroyPopperInstance();
        }
      }
      this.openChange.emit(isOpen);
    });
  }

  ngAfterViewInit(): void {
    this.dropdownButton = this.el.nativeElement.querySelector(
      '[learmoDropdownButton]',
    );
    this.dropdownMenu = this.el.nativeElement.querySelector(
      '[learmoDropdownMenu]',
    );

    if (this.dropdownMenu) {
      this.renderer.addClass(this.dropdownMenu, 'learmo-dropdown-menu');
      if (this.container === 'body') {
        document.body.appendChild(this.dropdownMenu);
      }
    }

    if (this.dropdownButton) {
      this.renderer.listen(
        this.dropdownButton,
        'click',
        (event: MouseEvent) => {
          event.stopPropagation();
          this.toggleDropdown();
        },
      );
    }
  }

  ngOnDestroy(): void {
    // Close the dropdown when the component is destroyed
    this.dropdownService.closeDropdownState(this.dropdownId);

    this.destroyPopperInstance();

    // Explicitly remove the 'show' class from the dropdown menu
    if (this.dropdownMenu) {
      this.renderer.removeClass(this.dropdownMenu, 'show');

      // Remove the menu from document.body to prevent orphaned elements
      if (
        this.container === 'body' &&
        document.body.contains(this.dropdownMenu)
      ) {
        document.body.removeChild(this.dropdownMenu);
      }
    }

    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    event.stopPropagation();
    this.toggleDropdown();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (
      this.autoClose &&
      this.isOpen() &&
      !this.el.nativeElement.contains(event.target)
    ) {
      // Check if click is inside the menu (if attached to body)
      if (
        this.dropdownMenu &&
        this.dropdownMenu.contains(event.target as Node)
      ) {
        return;
      }
      this.toggleDropdown(false);
    }
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (!this.isOpen()) return;

    const items = this.dropdownMenu?.querySelectorAll('a, button');
    const activeItem = Array.from(items || []).find((item: HTMLElement) =>
      item.classList.contains('active'),
    ) as HTMLElement;
    let index = items ? Array.from(items).indexOf(activeItem) : -1;

    switch (event.key) {
      case 'ArrowDown':
        index = (index + 1) % (items?.length || 0);
        break;
      case 'ArrowUp':
        index = (index - 1 + (items?.length || 0)) % (items?.length || 0);
        break;
      case 'Escape':
        this.toggleDropdown(false);
        return;
    }

    if (items?.[index]) {
      (items[index] as HTMLElement).focus();
      items[index].classList.add('active');
    }
  }

  closeAllDropdowns(): void {
    this.dropdownService.closeAllDropdowns([this.dropdownId]);
    // Ensure the current dropdown's state is updated
    this.isOpen.set(this.dropdownService.isDropdownOpen(this.dropdownId));
  }

  toggleDropdown(forceState?: boolean): void {
    this.closeAllDropdowns();
    const newState = forceState !== undefined ? forceState : !this.isOpen();
    this.isOpen.set(newState);
    this.dropdownService.toggleDropdownState(this.dropdownId, newState);
  }

  private createPopperInstance(): void {
    if (!this.dropdownButton || !this.dropdownMenu) {
      return;
    }
    this.popperInstance = createPopper(this.dropdownButton, this.dropdownMenu, {
      placement: this.placement,
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, 2],
          },
        },
        {
          name: 'preventOverflow',
          options: {
            boundary: 'viewport',
          },
        },
      ],
    });

    // Add ResizeObserver to update position when content changes size
    if (this.dropdownMenu) {
      this.resizeObserver = new ResizeObserver(() => {
        this.popperInstance?.update();
      });
      this.resizeObserver.observe(this.dropdownMenu);
    }
  }

  private destroyPopperInstance(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    if (this.popperInstance) {
      this.popperInstance.destroy();
      this.popperInstance = null;
    }
  }
}

// Directive for the dropdown button
@Directive({ selector: '[learmoDropdownButton]' })
export class LearmoDropdownButtonDirective {
  constructor(@Host() private parentDropdown: LearmoDropdownDirective) {}
}

// Directive for the dropdown menu
@Directive({ selector: '[learmoDropdownMenu]' })
export class LearmoDropdownMenuDirective implements AfterViewInit {
  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
  ) {}

  ngAfterViewInit(): void {
    // Initialization logic if needed
  }
}

// Directive for dropdown menu items
@Directive({ selector: '[learmoDropdownItem]' })
export class LearmoDropdownItemDirective {
  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private dropdown: LearmoDropdownDirective,
  ) {}

  @HostListener('click') onClick(): void {
    if (this.dropdown) {
      this.dropdown.toggleDropdown(false);
    }
  }

  @HostListener('mouseover') onHover(): void {
    // Hover logic if needed
  }

  @HostListener('mouseout') onMouseOut(): void {
    // Mouse out logic if needed
  }
}

export const LearmoDropdownDirectives = [
  LearmoDropdownDirective,
  LearmoDropdownButtonDirective,
  LearmoDropdownMenuDirective,
  LearmoDropdownItemDirective,
];
